import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "@studio-freight/lenis";
import { Toaster } from "sonner";

import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Categories from "@/pages/Categories";
import CategoryDetail from "@/pages/CategoryDetail";
import Cities from "@/pages/Cities";
import CityDetail from "@/pages/CityDetail";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import PickupGuide from "@/pages/PickupGuide";
import PricingGuide from "@/pages/PricingGuide";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import TermsConditions from "@/pages/TermsConditions";
import Landing from "@/pages/Landing";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/admin/AdminLogin";
import AdminDashboard from "@/admin/AdminDashboard";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function LenisProvider({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [isAdmin]);

  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <LenisProvider>
            <Toaster
              position="top-center"
              theme="light"
              toastOptions={{
                style: {
                  background: "#FFFFFF",
                  border: "1px solid #0B2545",
                  color: "#0B2545",
                  borderRadius: 0,
                  fontFamily: "'Inter', sans-serif",
                },
              }}
            />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/categories/:slug" element={<CategoryDetail />} />
              <Route path="/cities" element={<Cities />} />
              <Route path="/cities/:slug" element={<CityDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/pickup-guide" element={<PickupGuide />} />
              <Route path="/pricing-guide" element={<PricingGuide />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/lp" element={<Landing />} />
              <Route path="/lp/:slug" element={<Landing />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </LenisProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
